/*
 * Held replies outside the conversation that sent them (master-inbox plan, step 5, D6: "Undo
 * survives navigation"). A reply waits ten seconds on the server before it is claimed; the rep may
 * move to another conversation meanwhile. Each held reply keeps a watcher here, at the page, using the
 * same hook and the same cached status as the reply box (`use-reply-send.ts`), so its Undo, its
 * outcome and an unknown result follow the rep. The open conversation shows its own hold in the reply
 * box; every other one is an incumbent toast (ink plate, Undo) at the foot of the list.
 *
 * Nothing here sends. Undo asks the server to cancel; the outcome is whatever the server says.
 */
import { useCallback, useEffect, useState } from "react";
import { keepUnsent } from "./unsent-reply.ts";
import { useReplySend } from "./use-reply-send.ts";

export type ReplyHold = {
  readonly planId: string;
  readonly messageId: string | null;
  /** Who the reply goes to, as the toast names them. */
  readonly name: string;
  /** Where the reply's unsent text is kept, forgotten once it is sent. */
  readonly unsentKey: string;
};

/** Seconds left of a server-side hold, counted down on screen; the server's deadline decides. */
export function useSecondsLeft(until: string | undefined, running: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, [running]);
  if (!until) return 0;
  return Math.max(0, Math.min(10, Math.ceil((Date.parse(until) - now) / 1_000)));
}

export function useHeldSends() {
  const [holds, setHolds] = useState<readonly ReplyHold[]>([]);
  const add = useCallback(
    (hold: ReplyHold) =>
      setHolds((current) =>
        current.some((held) => held.planId === hold.planId) ? current : [...current, hold],
      ),
    [],
  );
  const drop = useCallback(
    (planId: string) => setHolds((current) => current.filter((held) => held.planId !== planId)),
    [],
  );
  return { holds, add, drop };
}

type Outcome = "queued" | "claimed" | "sent" | "cancelled" | "refused" | "unknown" | "waiting";

/** How long a settled toast stays, or null while it must stay: still held, or an unknown outcome. */
function linger(outcome: Outcome, open: boolean): number | null {
  if (outcome === "queued" || outcome === "claimed" || outcome === "waiting") return null;
  // The open conversation's reply box shows its own outcome; the toast has said its piece.
  if (open) return 0;
  if (outcome === "unknown") return null;
  return outcome === "sent" ? 5_000 : 10_000;
}

function HeldToast(props: {
  workspaceId: string;
  hold: ReplyHold;
  open: boolean;
  onDrop: (planId: string) => void;
  onOpen: (planId: string) => void;
}) {
  const { hold, onDrop } = props;
  const sending = useReplySend(props.workspaceId, hold.planId, hold.messageId);
  const send = sending.status?.send;
  const state = send?.state;
  const outcome: Outcome =
    sending.unknown || state === "uncertain" ? "unknown" : (state ?? "waiting");
  const seconds = useSecondsLeft(send?.holdUntil, outcome === "queued");
  useEffect(() => {
    if (state === "sent") keepUnsent(hold.unsentKey, null);
  }, [state, hold.unsentKey]);
  const stay = linger(outcome, props.open);
  useEffect(() => {
    if (stay === null) return;
    const timer = setTimeout(() => onDrop(hold.planId), stay);
    return () => clearTimeout(timer);
  }, [stay, onDrop, hold.planId]);
  if (props.open || outcome === "waiting") return null;
  const open = (
    <button
      type="button"
      className="lcr-bulk-toast-action"
      onClick={() => props.onOpen(hold.planId)}
    >
      Open
    </button>
  );
  return (
    <div className="lcr-bulk-toast lcr-i2-toast" data-outcome={outcome}>
      <HeldToastText
        name={hold.name}
        outcome={outcome}
        seconds={seconds}
        error={sending.error}
        looking={Boolean(send?.check?.nextAt)}
      />
      {outcome === "queued" ? (
        <button
          type="button"
          className="lcr-bulk-toast-action"
          disabled={sending.busy}
          onClick={() => void sending.undo()}
        >
          Undo
        </button>
      ) : (
        outcome !== "sent" && outcome !== "claimed" && open
      )}
    </div>
  );
}

/** One sentence per outcome; the countdown is drawn, and said once, never second by second. */
function HeldToastText(props: {
  name: string;
  outcome: Outcome;
  seconds: number;
  error: string | null;
  /** An unknown outcome LeadCue is still looking for in Bison (`send-reconciler.ts`). */
  looking: boolean;
}) {
  const { name, outcome } = props;
  if (props.error && outcome !== "queued") return <span>{props.error}</span>;
  switch (outcome) {
    case "queued":
      // Past its hold and not yet claimed: picked up again after an interruption, still unsent.
      if (props.seconds === 0) return <span>Sending your reply to {name}…</span>;
      return (
        <span>
          <span aria-hidden="true">
            Sending your reply to {name} in {props.seconds} s
          </span>
          <span className="lcr-visually-hidden">
            Your reply to {name} is held for 10 seconds. Undo is available.
          </span>
        </span>
      );
    case "claimed":
      return <span>Sending your reply to {name}…</span>;
    case "sent":
      return <span>Reply to {name} sent.</span>;
    case "cancelled":
      return <span>Send undone. Your reply to {name} is back in its conversation.</span>;
    case "refused":
      return <span>Your reply to {name} was not sent. Nothing left your outbox.</span>;
    default:
      if (props.looking) return <span>Checking Bison for your reply to {name}. Don't resend.</span>;
      return <span>Check Bison before resending: your reply to {name} may have gone out.</span>;
  }
}

/** The page's held replies, newest last, except the open conversation's, which its box shows. */
export function HeldToasts(props: {
  workspaceId: string;
  holds: readonly ReplyHold[];
  openId: string | null;
  onDrop: (planId: string) => void;
  onOpen: (planId: string) => void;
}) {
  return (
    <>
      {props.holds.map((hold) => (
        <HeldToast
          key={hold.planId}
          workspaceId={props.workspaceId}
          hold={hold}
          open={hold.planId === props.openId}
          onDrop={props.onDrop}
          onOpen={props.onOpen}
        />
      ))}
    </>
  );
}
